/* eslint-disable complexity */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import orderBy from 'lodash/orderBy';
import { useRouter } from 'next/router';

import 'dayjs/locale/ru';

import { doRequest } from '@r1-frontend/do-request';
import { disableVasWizard } from '@r1-frontend/api-domru/api-profile/v1/vas/disabled-vas-wizard';
import { EDuration, IPricesV2, IVasInfo } from '@r1-frontend/api-domru/api-profile/v1/vas/dto/ResponseDto';
import { createVasWizard } from '@r1-frontend/api-domru/api-profile/v2/vas/create-vas-wizard';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { IKebabItem } from '@r1-frontend/ui-react/components/KebabMenu';
import Throbber from '@r1-frontend/ui-react/components/loaders/Throbber';
import PopupResult, { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { IOption } from '@r1-frontend/ui-react/components/select/creatableSelect';
import { InfoCircleFlip } from '@r1-frontend/ui-react/components/svg/main';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';
import { IndentContainer, NonStaticContainer } from '@r1-frontend/ui-react/experimental/containers';
import { RubleSymbol } from '@r1-frontend/ui-react/experimental/purchase/cost/Cost/RubleSymbol';
import { AdvertisingLabelWithTooltip } from '@r1-frontend/ui-react/organisms/AdvertisingLabelWithTooltip';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';
import { pluralize } from '@r1-frontend/shared/helpers/pluralize';
import { dayPluralize, monthPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';

import PopupNoAuth from '~/src/components/Popups/NoAuth';
import { SelectPhonePopup } from '~/src/components/SelectPhonePopup';
import Widget from '~/src/components/Widget';
import TryAndBuyConnectionPopup from '~/src/features/domru-tv/packages/components/TryAndBuyConnectionPopup';
import { EWidgetKebabActionType } from '~/src/features/lk/vases/VasList/widgets/data';
import { INDEFINITELY_ACTIVE } from '~/src/features/lk/vases/VasList/widgets/helpers';
import { defaultPopupState } from '~/src/features/payments-abn/components/PopupResult';
import { addMonths, dateWithoutYear } from '~/src/helpers/date';
import { TState } from '~/src/store';
import { selectDomain } from '~/src/store/selectors/domain';
import { ISubscriptionAdditional } from '~/src/store/selectors/vas';

import DetailedSubscriptionPopup from './DetailedSubscriptionPopup';
import LabelAndActivity from './LabelAndActivity';
import { ESubscriptionsType, manualRedirectServiceIds } from './types';

import * as ST from './styled';

export const kebabActiveItems: IKebabItem[] = [
    {
        value: 'detail',
        label: 'Подробнее',
    },
    {
        value: 'disable',
        label: 'Отключить',
    },
];

interface IProps {
    subscription: IVasInfo<ISubscriptionAdditional>,
    queryId?: number,
    onClosePopup?: (status: EResultPopupStatus) => void,
}

const SubscriptionCard = ({ subscription, queryId, onClosePopup }: IProps): JSX.Element => {
    const {
        socId,
        serviceId,
        activeVas,
        buttonText,
        buttonLink = '',
        contentData,
        image,
        imageWebp,
        includedAgr,
        nameVas,
        priceVas,
        priceRa,
        pricesV2,
        activeTo,
        dateClose,
        closeDateRa,
        tryAndBuy,
        getPhone,
        advertising,
    } = subscription;

    const router = useRouter();

    const { accessToken, domain, isAuth, providerId } = useSelector((state: TState) => ({
        accessToken: state.auth.accessToken,
        domain: selectDomain(state),
        isAuth: state.auth.isAuth,
        providerId: state.city.provider.providerId,
    }));

    const closeVasDate = new Date(activeTo !== INDEFINITELY_ACTIVE ? activeTo : dateClose);

    const [isIncludesPopupOpen, setIsIncludesPopupOpen] = useState(false);
    const [isPhonePopupOpen, setIsPhonePopupOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [popupState, setPopupState] = useState(defaultPopupState);
    const [clientPhone, setClientPhone] = useState('');
    const [detailedPopupOpen, setDetailedPopupOpen] = useState(false);
    const [popupUnAuth, setPopupUnAuth] = useState(false);
    const [isTnBPopupOpen, setIsTnBPopupOpen] = useState(false);

    const isRewriteSubscription = serviceId ? manualRedirectServiceIds.includes(serviceId) : false;
    const withKebabMenu = !!activeVas && (activeTo === INDEFINITELY_ACTIVE || activeTo === null) && !includedAgr;
    const pathToDetailedPage = `/domru-tv/subscriptions/${contentData?.alias}`;

    const closeDate = useMemo(() => dateClose ? dayjs(dateClose).format('DD.MM.YYYY') : '', [dateClose]);
    const isIndefinitelySub = closeDateRa === 'бессрочно';


    const text = (includedAgr || activeVas)
        ? isRewriteSubscription
            ? 'Подключено'
            : buttonText
        : 'Подключить';

    const subscriptionsWithoutTNB = pricesV2?.find(item => !item.raGroups.includes(ESubscriptionsType.TRYANDBUYSUBSCRIPTIONS));

    const onDetailHandler = useCallback(() => {
        if (isAuth) {
            router.push(pathToDetailedPage);
        } else {
            setDetailedPopupOpen(true);
            router.replace(`/domru-tv/packages?subscriptionId=${serviceId}`, undefined, {
                shallow: true,
            });
        }

        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.vas,
            action: 'click details btn',
            label: `subscription: ${nameVas}`,
        });
    }, []);

    const getActiveToDate = subscriptionsWithoutTNB?.lifeTimePromo === null
        ? addMonths(new Date(), 1)
        : addMonths(new Date(), Number(subscriptionsWithoutTNB?.lifeTimePromo));

    const activeToDate = getActiveToDate?.toLocaleString('ru-RU').split(',')[0];

    const sortedSubscriptionPrice = orderBy(subscription.pricesV2 as IPricesV2[], 'discount');

    const tryAndBuyLabelText = useMemo(() => {
        if (closeDateRa && !isIndefinitelySub) {
            return `промо-цена до ${dateWithoutYear(new Date(closeDateRa))}`;
        }

        if (tryAndBuy) {
            const { duration, durationTypeId } = tryAndBuy;
            const periodText = durationTypeId === EDuration.DAYS ? pluralize(duration, dayPluralize) : pluralize(duration, monthPluralize);

            return `${duration} ${periodText} бесплатно`;
        }
    }, [closeDateRa, tryAndBuy]);

    const onConnectHandler = useCallback(() => {
        if (isAuth) {
            if (!tryAndBuy && !getPhone && (pricesV2 && pricesV2.length > 1)) {
                router.push(pathToDetailedPage);
            } else if (getPhone) {
                setIsPhonePopupOpen(true);
            } else if (pricesV2?.length === 1) {
                setDetailedPopupOpen(true);
            } else {
                setIsIncludesPopupOpen(true);
            }
        } else {
            setPopupUnAuth(true);
        }

        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.vas,
            action: 'click connect btn',
            label: `subscription: ${nameVas}`,
        });
    }, [isAuth, router, tryAndBuy, nameVas]);

    const widgetButtonClickHandler = () => {
        switch (true) {
            case !!activeVas:
                redirectButtonLink();
                break;
            case !!tryAndBuy:
                setIsTnBPopupOpen(true);
                break;
            default:
                onConnectHandler();
                break;
        }
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.vas,
            action: 'click connect btn',
            label: `subscription: ${nameVas}`,
        });
    };

    const handleIncludesPopupClose = () => {
        setIsIncludesPopupOpen(false);
        setIsPhonePopupOpen(false);
    };

    const onCloseHandler = useCallback(() => {
        if (popupState.type && onClosePopup) {
            onClosePopup(popupState.type);
        }

        setPopupState(defaultPopupState);
    }, [onClosePopup, popupState.type]);

    useEffect(() => {
        (queryId === serviceId) && setDetailedPopupOpen(true);
    }, [queryId, serviceId]);

    const onClickDisconnect = useCallback(() => {
        setIsIncludesPopupOpen(true);
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.vas,
            action: 'click deactivation btn',
            label: `subscription: ${nameVas}`,
        });
    }, [nameVas]);

    const onSelectMenuHandler = useCallback((item?: IKebabItem) => {
        switch (item?.value) {
            case EWidgetKebabActionType.DETAIL:
                onDetailHandler();
                break;
            case EWidgetKebabActionType.DISABLE:
                onClickDisconnect();
                break;
        }
    }, [onClickDisconnect, onDetailHandler]);

    const onSendDisconnectResult = useCallback(async() => {
        setIsLoading(true);

        const resp = await doRequest.apiProfile(disableVasWizard({
            socId,
            activeTo: closeVasDate
                ? dayjs(closeVasDate).format('DD.MM.YYYY')
                : undefined,
        }));

        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.vas,
            action: `${resp.isSuccess ? 'success' : 'error'} deactivation`,
            label: `subscription: ${nameVas}`,
        });

        setPopupState({
            isOpen: true,
            type: resp.isSuccess ? EResultPopupStatus.SUCCESS : EResultPopupStatus.ERROR,
            title: resp.isSuccess
                ? `Вы отключили подписку ${nameVas}`
                : 'Что-то пошло не так. Попробуйте ещё раз.',
            text: resp.isSuccess ? <ST.Text>Она будет доступна до {closeDate}.</ST.Text> : '',
        });

        handleIncludesPopupClose();
        setIsLoading(false);
    }, [socId, nameVas]);

    const redirectButtonLink = useCallback(() => {
        window.open(`${buttonLink}?access_token=${accessToken}&region=${domain}`, '_blank', 'noopener noreferrer');

        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.vas,
            action: 'click_open_subscription_detail_btn',
            label: `subscription: ${nameVas}`,
        });
    }, [nameVas, buttonLink]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const onSendResult = useCallback(async() => {
        setIsLoading(true);

        const phone = clientPhone.length === 11 ? clientPhone : undefined;
        const contactId = clientPhone.length < 11 ? +clientPhone : undefined;

        const clientParams = {
            contactId,
            phone,
        };

        const params = (!!getPhone || isRewriteSubscription) ? clientParams : undefined;

        const resp = await doRequest.apiProfile(createVasWizard({ socId, ...params }));

        handleIncludesPopupClose();
        setDetailedPopupOpen(false);
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.vas,
            action: `connect ${resp.isSuccess ? 'success' : 'error'}`,
            label: `subscription: ${nameVas} | period: ${sortedSubscriptionPrice && sortedSubscriptionPrice[0].duration} month | discount: ${sortedSubscriptionPrice && sortedSubscriptionPrice[0].discount}`,
        });

        setPopupState({
            isOpen: true,
            type: resp.isSuccess ? EResultPopupStatus.SUCCESS : EResultPopupStatus.ERROR,
            title: resp.isSuccess ? `Вы подключили подписку ${nameVas}${activeTo === null ? '' : ` до ${activeTo}.`}` : 'Что-то пошло не так. Попробуйте ещё раз.',
            text: resp.isSuccess ? `Стоимость — ${priceRa || priceVas} ${RubleSymbol}` : '',
        });
        setIsLoading(false);
    }, [socId, clientPhone, isRewriteSubscription, getPhone]);

    const onTryAndBuyConnect = async(phone: IOption) => {
        if (!tryAndBuy) {
            return;
        }

        setIsLoading(true);
        const { duration, durationTypeId, raId, discount } = tryAndBuy;
        const { value, label } = phone;

        const phoneParam = value === label ? { phone: label } : { contactId: value };

        const { isSuccess } = await doRequest.apiProfile(createVasWizard({
            socId,
            flagId: raId,
            ...phoneParam,
        }));

        const endDate = dayjs()
            .add(
                duration,
                durationTypeId === EDuration.DAYS ? 'day' : 'month',
            )
            .locale('ru')
            .format('DD MMMM YYYY');


        setPopupState({
            isOpen: true,
            type: isSuccess ? EResultPopupStatus.SUCCESS : EResultPopupStatus.ERROR,
            title: isSuccess ? `Вы подключили подписку ${nameVas}` : 'Что-то пошло не так. Попробуйте ещё раз.',
            text: isSuccess ? `Смотрите до ${endDate} года бесплатно, затем ${priceRa || priceVas} ${RubleSymbol}/мес.` : '',
        });

        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.vas,
            action: `connect ${isSuccess ? 'success' : 'error'}`,
            label: `subscription: ${nameVas} | period: ${duration} ${durationTypeId === EDuration.DAYS ? 'days' : 'month'} | discount: ${discount}`,
        });

        setIsLoading(false);
        setIsTnBPopupOpen(false);
    };

    const phonePopupTitle = `Укажите номер для подключения подписки ${nameVas}`;
    const phonePopupDescription = `Введите номер, на который не оформлена на текущий момент подписка ${nameVas}. 
    Если такого номера нет, оформить её не получится. После перехода на новый тариф, ваша подписка ${nameVas} 
    привяжется к указанному номеру телефона.`;

    return (
        <>
            <SelectPhonePopup
                isOpen={isPhonePopupOpen}
                onClose={handleIncludesPopupClose}
                subscription={subscription}
                accessToken={accessToken}
                domain={domain}
                providerId={providerId}
                setPhone={setClientPhone}
                onSendResult={onSendResult}
                setIsPhonePopupOpen={setIsPhonePopupOpen}
                isLoading={isLoading}
                title={phonePopupTitle}
                description={phonePopupDescription}
            />
            <ST.IncludesPopup
                isOpen={isIncludesPopupOpen}
                fullWidth={false}
                width={600}
                onClose={handleIncludesPopupClose}
            >
                <ST.PopupInner>
                    <ST.PopupTitle>{activeVas ? 'Отключить' : 'Подключить'} подписку {nameVas}?</ST.PopupTitle>
                    <div>
                        <ST.PopupDescription>{activeVas
                            ? <ST.Text>До {closeDate} вы можете смотреть кино по этой подписке.</ST.Text>
                            : `Отключить её можно будет только после ${activeToDate}`}
                        </ST.PopupDescription>
                    </div>
                    <ST.PopupButtonBlock>
                        <Button
                            bType={'action'}
                            onClick={activeVas ? () => onSendDisconnectResult() : () => onSendResult()}
                            Icon={isLoading && <Throbber color={COLORS.BgMain} />}
                        >
                            {!isLoading && (activeVas ? 'Отключить' : 'Подключить')}
                        </Button>
                        <Button bType={'secondary'} onClick={handleIncludesPopupClose}>
                            Отменить
                        </Button>
                    </ST.PopupButtonBlock>
                </ST.PopupInner>
            </ST.IncludesPopup>
            <Widget>
                <Widget.ImageBlock>
                    <WebpImage urlWebp={imageWebp}>
                        <Widget.Image
                            src={image}
                            alt={nameVas}
                        />
                    </WebpImage>
                    {advertising &&
                        <NonStaticContainer
                            $united={{ $bottom: 12, $right: 12 }}
                        >
                            <AdvertisingLabelWithTooltip
                                companyName={advertising.companyName}
                                erid={advertising.erid}
                                tooltip={{
                                    position: 'top-right',
                                }}
                            />
                        </NonStaticContainer>
                    }
                </Widget.ImageBlock>
                <Widget.ContentBlock>
                    {includedAgr
                        ? <Widget.PeriodData data-test='subs-included'>Входит в ваш тариф</Widget.PeriodData>
                        : <LabelAndActivity
                            isActive={!!activeVas}
                            activeDate={activeTo}
                            labelText={tryAndBuyLabelText}
                        />
                    }
                    <Widget.Name>{nameVas}</Widget.Name>
                    {!includedAgr && contentData && (
                        <ST.PackageDesc dangerouslySetInnerHTML={{ __html: contentData.body }} />
                    )}
                    <Widget.FooterBlock>
                        {!includedAgr && (
                            <ST.WidgetPrice
                                isPromoPrice={tryAndBuy?.cost !== undefined || priceRa !== null}
                                data-test='subs-price'
                            >
                                {tryAndBuy?.cost ?? priceRa ?? priceVas} ₽/мес
                            </ST.WidgetPrice>
                        )}
                        <Button
                            bType={(includedAgr || activeVas) ? 'secondary' : 'primary'}
                            size={'small'}
                            disabled={isRewriteSubscription && activeVas === 1}
                            onClick={widgetButtonClickHandler}
                        >
                            {text}
                        </Button>
                        {withKebabMenu
                            ? <ST.WidgetKebab
                                menuItems={kebabActiveItems}
                                onSelectMenu={onSelectMenuHandler}
                            />
                            : <IndentContainer $united={[0, 0, 0, 8]}>
                                <Button
                                    bType={'secondary'}
                                    size={'medium'}
                                    Icon={<InfoCircleFlip size={20}/>}
                                    rounded
                                    onClick={onDetailHandler}
                                />
                            </IndentContainer>
                        }
                    </Widget.FooterBlock>
                </Widget.ContentBlock>
            </Widget>
            <PopupResult {...popupState} onClose={onCloseHandler} isLoading={true}>
                <Button
                    bType={'action'}
                    size={'large'}
                    onClick={onCloseHandler}
                >
                    Закрыть
                </Button>
            </PopupResult>
            <DetailedSubscriptionPopup
                isOpen={detailedPopupOpen}
                onClose={() => {
                    setDetailedPopupOpen(false);
                    router.replace('/domru-tv/packages', undefined, {
                        shallow: true,
                    });
                }}
                subscription={subscription}
                onSendResult={onSendResult}
                onClickUnAuth={setPopupUnAuth}
                isAuth={isAuth}
                popupImage={image}
                setIsPhonePopupOpen={setIsPhonePopupOpen}
                isRewriteSubscription={isRewriteSubscription}
            />
            {(!activeVas && tryAndBuy) && (
                <TryAndBuyConnectionPopup
                    isOpen={isTnBPopupOpen}
                    isConnectionInProgress={isLoading}
                    title={`Подключить ${nameVas}`}
                    labelText={tryAndBuyLabelText ?? ''}
                    onConnect={onTryAndBuyConnect}
                    onClose={() => setIsTnBPopupOpen(false)}
                />
            )}
            <PopupNoAuth close={() => setPopupUnAuth(false)} open={popupUnAuth} />
        </>
    );
};

export default SubscriptionCard;
